/**
 * «Funksjoner» in words a volunteer can act on (PM gaps B2/B5/B9, 2026-11-01) — pure, so a test holds it without a browser.
 *
 * A row from GET /capabilities says WHY a capability is not in use (`reason`). Each reason names what is missing and who
 * can fix it: another function (by its Norwegian name), the privacy page, keys only Min Masjid can enter, or the
 * subscription. The filter chips sort the rows into the same four answers.
 */
import type { Body } from './api';

export type FeatureFilter = 'all' | 'can' | 'waiting' | 'excluded';
export const FILTERS: [FeatureFilter, string][] = [['all', 'Alle'], ['can', 'Kan slås på'], ['waiting', 'Venter'], ['excluded', 'Ikke i abonnementet']];

const reasonOf = (c: Body): string => String((c && c.reason) || '');
/** Included in the plan, but the mosque's privacy page is not approved for these records (core capabilities, privacy gate). */
export const waitsForPrivacy = (c: Body): boolean => reasonOf(c).startsWith('privacy_not_ready:');
/** The other capability this one waits for, or null. */
export const dependency = (c: Body): string | null => (reasonOf(c).startsWith('depends:') ? reasonOf(c).slice('depends:'.length) : null);

/** Which chip a row belongs to ('all' holds every row; a row that is simply on is in none of the other three). */
export function filterOf(c: Body): Exclude<FeatureFilter, 'all'> | null {
  const r = reasonOf(c);
  // A suspension waits for Masjid too: it is not «not in the subscription».
  if (waitsForPrivacy(c) || r.startsWith('depends:') || r.startsWith('missing:') || r.startsWith('config:') || r === 'not_configured' || r === 'suspended') return 'waiting';
  if (r === 'blocked' || r === 'not_entitled' || !c.entitled) return 'excluded';
  if (!c.enabled && c.manageable) return 'can';
  return null;
}

export function matches(c: Body, f: FeatureFilter): boolean { return f === 'all' || filterOf(c) === f; }

/** Why it is not in use, in words; `labelOf` names another capability. '' when it is on. */
export function reasonText(c: Body, labelOf: (key: string) => string): string {
  const r = reasonOf(c);
  if (!r) return '';
  if (r === 'not_entitled') return 'ikke inkludert i abonnementet';
  if (r === 'suspended') return 'midlertidig stanset av Masjid';
  if (r === 'disabled') return 'slått av';
  if (r === 'blocked') return 'ikke klar';
  if (r.startsWith('privacy_not_ready:')) return 'venter på personvern';
  if (r === 'not_configured' || r.startsWith('missing:')) return 'Masjid må legge inn nøkler – kontakt Masjid';
  if (r.startsWith('depends:')) return `venter på «${labelOf(r.slice('depends:'.length))}»`;
  if (r.startsWith('config:')) return 'ugyldige innstillinger – kontakt Masjid';
  return r;
}

/** Whether «Slå på den også» can be offered for the capability `dep` that another one waits for. */
export function canAlsoSwitch(dep: Body | undefined, maySwitch: boolean): boolean {
  return Boolean(maySwitch && dep && dep.kind !== 'provider' && dep.manageable && dep.entitled && !dep.enabled && !(dep.entitlement && dep.entitlement.suspended));
}

/** A page that tells staff to switch something on says who can (PM gaps B3): Funksjoner is read-only below super admin. */
export function switchOnHint(label: string, maySwitch: boolean): string {
  // Under Funksjoner it also says what else it waits for (another function, the privacy page, the subscription).
  return maySwitch ? `Slå på «${label}» under Funksjoner – der står det også om den venter på noe annet.`
    : `Be en superadministrator slå på «${label}» under Funksjoner – der står det også om den venter på noe annet.`;
}

export interface SetupLine { key: string; title: string; why: string; to: string }
const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;

/**
 * «Oppsett» on Oversikt (PM gaps B4): what is missing for the mosque to use what it has — only lines that are true now.
 * `caps`: GET /capabilities `items` (null: not asked or failed). `privacy`: GET /privacy-page (null likewise).
 */
export function setupLines(caps: Body[] | null, privacy: Body | null): SetupLine[] {
  const out: SetupLine[] = [];
  const features = (caps || []).filter((c) => c.kind !== 'provider');
  if (privacy && privacy.onPlatform && privacy.mode === 'placeholder') {
    out.push({ key: 'privacy-page', title: 'Ingen egen personvernside',
      why: 'Besøkende ser en nøytral side. Funksjoner som lagrer opplysninger om folk, kan ikke brukes før moskeen har en godkjent side.', to: 'privacy' });
  }
  const privacyWait = features.filter(waitsForPrivacy).length;
  if (privacyWait) {
    out.push({ key: 'privacy-wait', title: `${n(privacyWait, 'funksjon venter', 'funksjoner venter')} på godkjent personvernside`,
      why: 'De er med i abonnementet, men Masjid må godkjenne personvernsiden for dem først.', to: 'privacy' });
  }
  const byKey = new Map((caps || []).map((c) => [c.key, c]));
  const vipps = byKey.get('provider.vipps_epayment'), gifts = byKey.get('donations');
  if (vipps && !vipps.on && gifts && gifts.on) {
    out.push({ key: 'vipps', title: 'Vipps-betaling er ikke satt opp',
      why: 'Gaver med Vipps virker ikke før Masjid har koblet moskeens Vipps-avtale.', to: 'donations' });
  }
  const waiting = features.filter((c) => c.enabled && dependency(c)).length;
  if (waiting) {
    out.push({ key: 'depends', title: `${n(waiting, 'funksjon er slått på, men venter', 'funksjoner er slått på, men venter')} på en annen`,
      why: 'Se under Funksjoner hva de venter på.', to: 'features' });
  }
  const keys = features.filter((c) => c.enabled && c.entitled && /^missing:|^not_configured$/.test(String(c.reason || ''))).length;
  if (keys) {
    out.push({ key: 'keys', title: `${n(keys, 'funksjon mangler', 'funksjoner mangler')} nøkler fra Masjid`,
      why: 'Masjid må legge inn nøklene før de virker. Kontakt Masjid.', to: 'features' });
  }
  return out;
}
